import React, { useState, useEffect } from "react";
import Avatar from "@mui/joy/Avatar";
import Box from "@mui/joy/Box";
import Chip from "@mui/joy/Chip";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import Menu from "@mui/joy/Menu";
import MenuButton from "@mui/joy/MenuButton";
import MenuItem from "@mui/joy/MenuItem";
import Dropdown from "@mui/joy/Dropdown";
import CircularProgress from "@mui/joy/CircularProgress";
import Snackbar from "@mui/joy/Snackbar";

import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { gql, useQuery, useMutation } from "@apollo/client";
import Pagination from "../components/Pagination";
import { usePagination } from "../components/usePagination";
import {
  useActionCable,
  useChannel,
} from "@aersoftware/react-use-action-cable";
import { getWebSocketUrl } from "../utils/actionCableUrl";

const PROFILE_UPDATE_REQUESTS_QUERY = gql`
  query ProfileUpdateRequests(
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    profileUpdateRequests(
      first: $first
      after: $after
      last: $last
      before: $before
    ) {
      edges {
        node {
          createdAt
          requestedChanges
          reviewedAt
          state
          id
          requestor {
            firstName
            lastName
            email
          }
          reviewer {
            firstName
            lastName
            email
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

const REVIEW_PROFILE_UPDATE_REQUEST_MUTATION = gql`
  mutation ReviewProfileUpdateRequest(
    $input: ReviewProfileUpdateRequestInput!
  ) {
    reviewProfileUpdateRequest(input: $input) {
      profileUpdateRequest {
        id
        state
        reviewedAt
        reviewer {
          firstName
          lastName
          email
        }
      }
    }
  }
`;

const RowMenu = ({ row }) => {
  const [reviewProfileUpdateRequest] = useMutation(
    REVIEW_PROFILE_UPDATE_REQUEST_MUTATION
  );
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleError = (error) => {
    setErrorMessage(error?.message || "An error occurred");
    setOpen(true);
  };

  return (
    <>
      <Dropdown>
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{
            root: { variant: "plain", color: "neutral", size: "sm" },
          }}
        >
          <MoreHorizRoundedIcon />
        </MenuButton>
        <Menu size="sm" sx={{ minWidth: 140 }}>
          <MenuItem
            color="success"
            onClick={() =>
              reviewProfileUpdateRequest({
                variables: { input: { id: row.id, action: "ACCEPT" } },
                onError: handleError,
              })
            }
          >
            Approve
          </MenuItem>
          <MenuItem
            color="danger"
            onClick={() =>
              reviewProfileUpdateRequest({
                variables: { input: { id: row.id, action: "REJECT" } },
                onError: handleError,
              })
            }
          >
            Reject
          </MenuItem>
        </Menu>
      </Dropdown>
      <Snackbar
        open={open}
        color="danger"
        variant="soft"
        onClose={() => setOpen(false)}
        autoHideDuration={3000}
      >
        {errorMessage}
      </Snackbar>
    </>
  );
};

const requestedChangesLabels = {
  city: "City",
  country: "Country",
  date_of_birth: "Date of Birth",
  email: "Email",
  employer: "Employer",
  employment_status: "Employment Status",
  first_name: "First Name",
  gender: "Gender",
  id_passport_number: "ID/Passport Number",
  last_name: "Last Name",
  marital_status: "Marital Status",
  middle_name: "Middle Name",
  nationality: "Nationality",
  occupation: "Occupation",
  phone_number: "Phone Number",
  postal_code: "Postal Code",
  state_province: "State/Province",
  street_address: "Street Address",
};

const Row = (props: { row: any; key?: any }) => {
  const { row } = props;
  const [open, setOpen] = useState(false);

  return (
    <React.Fragment>
      <tr>
        <td>
          <IconButton
            aria-label="expand row"
            variant="plain"
            color="neutral"
            size="sm"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </td>
        <td>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Avatar
              size="sm"
              alt={`${row.requestor.firstName} ${row.requestor.lastName}`}
            />
            <div>
              <Typography level="body-xs">
                {row.requestor.firstName} {row.requestor.lastName}
              </Typography>
              <Typography
                level="body-xs"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "200px",
                }}
              >
                {row.requestor.email}
              </Typography>
            </div>
          </Box>
        </td>
        <td>
          <Typography level="body-xs">
            {new Intl.DateTimeFormat("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }).format(new Date(row.createdAt))}
          </Typography>
        </td>
        <td>
          <Chip
            variant="soft"
            size="sm"
            color={
              {
                pending: "warning",
                accepted: "success",
                rejected: "danger",
              }[row.state] as ColorPaletteProp
            }
          >
            {row.state.toUpperCase()}
          </Chip>
        </td>
        <td>
          {row.reviewer && (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Avatar
                size="sm"
                alt={`${row.reviewer.firstName} ${row.reviewer.lastName}`}
              />
              <div>
                <Typography level="body-xs">
                  {row.reviewer.firstName} {row.reviewer.lastName}
                </Typography>
                <Typography
                  level="body-xs"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "200px",
                  }}
                >
                  {row.reviewer.email}
                </Typography>
              </div>
            </Box>
          )}
        </td>
        <td>
          {row.reviewedAt && (
            <Typography level="body-xs">
              {new Intl.DateTimeFormat("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }).format(new Date(row.reviewedAt))}
            </Typography>
          )}
        </td>
        <td>
          {row.state === "pending" && (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <RowMenu row={row} />
            </Box>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5}>
            <Table size="sm">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>From</th>
                  <th>To</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(row.requestedChanges).map(([key, value]) => (
                  <tr key={key}>
                    <td>{requestedChangesLabels[key]}</td>
                    <td>{value.from}</td>
                    <td>{value.to}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

const UserProfileChangeRequestsTable = () => {
  const [pageSize] = useState(10);
  const { data, loading, error, fetchMore, refetch } = useQuery(
    PROFILE_UPDATE_REQUESTS_QUERY,
    {
      variables: {
        first: pageSize,
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }
  );

  const {
    pageInfo,
    isLoadingMore,
    handlePageChange,
    canGoNext,
    canGoPrevious,
  } = usePagination({
    pageSize,
    data,
    fetchMore,
    getPageInfo: (data) => data.profileUpdateRequests.pageInfo,
    updateQuery: (prev, { fetchMoreResult }) => {
      if (!fetchMoreResult) return prev;
      return {
        profileUpdateRequests: {
          ...fetchMoreResult.profileUpdateRequests,
          edges: [...fetchMoreResult.profileUpdateRequests.edges],
        },
      };
    },
  });

  // Setup WebSocket connection
  const { actionCable } = useActionCable(getWebSocketUrl());
  const channel = useChannel(actionCable);

  useEffect(() => {
    channel.subscribe(
      { channel: "ApplicationChannel" },
      {
        received: (wsData) => {
          if (wsData.type === "profile_update_request_updated") {
            refetch();
          }
        },
      }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [refetch]);

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Typography level="body-lg" color="danger">
          Error loading data: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th style={{ width: 40, padding: "12px 6px" }}></th>
              <th style={{ width: 240, padding: "12px 6px" }}>Requestor</th>
              <th style={{ width: 140, padding: "12px 6px" }}>Date</th>
              <th style={{ width: 140, padding: "12px 6px" }}>Status</th>
              <th style={{ width: 240, padding: "12px 6px" }}>Reviewer</th>
              <th style={{ width: 140, padding: "12px 6px" }}>Reviewed At</th>
              <th style={{ width: 40, padding: "12px 6px" }}></th>
            </tr>
          </thead>
          <tbody>
            {loading || isLoadingMore ? (
              <tr>
                <td colSpan={7}>
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress size="sm" />
                  </Box>
                </td>
              </tr>
            ) : (
              data?.profileUpdateRequests?.edges?.map(({ node: row }) => (
                <Row key={row.id} row={row} />
              ))
            )}
          </tbody>
        </Table>
      </Sheet>
      <Pagination
        pageInfo={pageInfo}
        isLoadingMore={isLoadingMore}
        onPageChange={handlePageChange}
        canGoNext={canGoNext}
        canGoPrevious={canGoPrevious}
      />
    </React.Fragment>
  );
};

const UserProfileChangeRequests = () => {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h2" component="h1">
          User Profile Change Requests
        </Typography>
      </Box>
      <UserProfileChangeRequestsTable />
    </>
  );
};

export default UserProfileChangeRequests;
